import { useState } from 'react';
import { LogOut, FileText } from 'lucide-react';
import { generateMoM } from '../lib/api';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../hooks/useToast';
import { Link } from 'wouter';

interface EndSessionChipProps {
  sessionId: string;
  messageCount: number;
  onSessionEnd: () => void;
}

const EndSessionChip = ({ sessionId, messageCount, onSessionEnd }: EndSessionChipProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const { getToken } = useAuth();
  const { success, error } = useToast();

  const handleEndSession = async () => {
    try {
      setIsGenerating(true);
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      // Generate MoM if session has enough messages
      if (messageCount >= 3) {
        try {
          await generateMoM(sessionId, token);
          success('Session ended and MoM generated!');
        } catch (err: any) {
          if (err.message?.includes('already been generated')) {
            success('Session ended - MoM already exists');
          } else {
            // Still end session even if MoM fails
            success('Session ended');
            console.warn('MoM generation failed:', err);
          }
        }
      } else {
        success('Session ended');
      }

      onSessionEnd();
    } catch (err) {
      error('Failed to end session');
      console.error('Error ending session:', err);
    } finally {
      setIsGenerating(false);
      setShowOptions(false);
    }
  };

  if (messageCount === 0) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowOptions(!showOptions)}
        disabled={isGenerating}
        className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 disabled:opacity-50 transition-colors"
      >
        {isGenerating ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-600 border-t-transparent" />
        ) : (
          <LogOut className="h-4 w-4" />
        )}
        End Session
      </button>

      {showOptions && (
        <div className="absolute top-full right-0 mt-2 w-48 rounded-2xl border border-slate-200 bg-white shadow-lg z-10">
          <div className="p-2 space-y-1">
            <button
              onClick={handleEndSession}
              disabled={isGenerating}
              className="w-full flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <FileText className="h-4 w-4" />
              End & Generate MoM
            </button>
            <Link
              href="/archive"
              className="w-full flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <FileText className="h-4 w-4" />
              View MoM Archive
            </Link>
            <button
              onClick={() => setShowOptions(false)}
              className="w-full flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EndSessionChip;
